import { ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { UserOptionsService } from '@/services/user-options'
import type { HourType } from '@/types/hour-type'
import type { Project } from '@/types/project'
import type { User } from '@/types/user'
import type { UserOptionsMode } from '@/types/user-options'
import { MultiSelectSearchDialog } from './MultiSelectSearchDialog'

interface UserOptionsSheetProps {
	user: User | null
	open: boolean
	onOpenChange: (open: boolean) => void
	projects: Project[]
	hourTypes: HourType[]
	companyID: string
}

export function UserOptionsSheet({ user, open, onOpenChange, projects, hourTypes, companyID }: UserOptionsSheetProps) {
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [selectedProjectIDs, setSelectedProjectIDs] = useState<string[]>([])
	const [selectedHourTypeIDs, setSelectedHourTypeIDs] = useState<string[]>([])
	const [mode, setMode] = useState<UserOptionsMode>('both')
	const [showProjectsDialog, setShowProjectsDialog] = useState(false)
	const [showHourTypesDialog, setShowHourTypesDialog] = useState(false)

	const fetchOptions = useCallback(async () => {
		if (!user) return
		try {
			setLoading(true)
			const options = await UserOptionsService.getUserOptions(user.id, companyID)
			setSelectedProjectIDs(options.projects.map((p) => p.id))
			setSelectedHourTypeIDs(options.hourTypes.map((ht) => ht.id))
			setMode(options.mode)
		} catch (err) {
			console.error(err)
			toast.error('Error al cargar opciones del usuario')
		} finally {
			setLoading(false)
		}
	}, [user, companyID])

	useEffect(() => {
		if (open && user) {
			fetchOptions()
		}
	}, [open, user, fetchOptions])

	const handleSave = async () => {
		if (!user) return
		try {
			setSaving(true)
			await UserOptionsService.setUserOptions({
				userID: user.id,
				companyID,
				projectIDs: selectedProjectIDs,
				hourTypeIDs: selectedHourTypeIDs,
				mode,
			})
			toast.success('Opciones del usuario guardadas correctamente')
			onOpenChange(false)
		} catch (err) {
			console.error(err)
			toast.error(err instanceof Error ? err.message : 'Error al guardar opciones del usuario')
		} finally {
			setSaving(false)
		}
	}

	return (
		<>
			<Sheet open={open} onOpenChange={onOpenChange}>
				<SheetContent className="overflow-y-auto">
					<SheetHeader className="space-y-2 pb-4">
						<SheetTitle>Opciones de usuario</SheetTitle>
						<SheetDescription>
							Configura los proyectos, tipos de hora y modo de fichaje para{' '}
							<span className="font-medium">{user?.name}</span>
						</SheetDescription>
					</SheetHeader>
					{loading ? (
						<div className="flex-1 py-4 space-y-4 px-4">
							<Skeleton className="h-8 w-full" />
							<Skeleton className="h-12 w-full" />
							<Skeleton className="h-8 w-full" />
							<Skeleton className="h-12 w-full" />
						</div>
					) : (
						<div className="flex-1 py-4 space-y-6 px-4">
							{/* Modo de fichaje */}
							<div className="space-y-2">
								<Label className="text-sm font-semibold">Modo de fichaje</Label>
								<Select value={mode} onValueChange={(value) => setMode(value as UserOptionsMode)}>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="both">Ambos (Timer + Bloque)</SelectItem>
										<SelectItem value="timer">Solo Timer</SelectItem>
										<SelectItem value="block">Solo Bloque</SelectItem>
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">
									Define cómo puede fichar el usuario en la aplicación
								</p>
							</div>

							{/* Proyectos */}
							<div className="space-y-2">
								<Label className="text-sm font-semibold">Proyectos</Label>
								<Button
									variant="outline"
									className="w-full justify-between font-normal"
									onClick={() => setShowProjectsDialog(true)}
								>
									<span className="text-sm">
										{selectedProjectIDs.length} de {projects.length} seleccionados
									</span>
									<ChevronRight className="h-4 w-4 shrink-0 opacity-50" />
								</Button>
							</div>

							{/* Tipos de hora */}
							<div className="space-y-2">
								<Label className="text-sm font-semibold">Tipos de hora</Label>
								<Button
									variant="outline"
									className="w-full justify-between font-normal"
									onClick={() => setShowHourTypesDialog(true)}
								>
									<span className="text-sm">
										{selectedHourTypeIDs.length} de {hourTypes.length} seleccionados
									</span>
									<ChevronRight className="h-4 w-4 shrink-0 opacity-50" />
								</Button>
							</div>
						</div>
					)}
					<SheetFooter className="pt-4">
						<Button onClick={handleSave} disabled={saving || loading}>
							{saving ? 'Guardando...' : 'Guardar opciones'}
						</Button>
					</SheetFooter>
				</SheetContent>
			</Sheet>

			<MultiSelectSearchDialog
				open={showProjectsDialog}
				onOpenChange={setShowProjectsDialog}
				title="Seleccionar proyectos"
				description="Marca los proyectos que el usuario puede registrar"
				items={projects}
				selectedIds={selectedProjectIDs}
				onSelectionChange={setSelectedProjectIDs}
			/>
			<MultiSelectSearchDialog
				open={showHourTypesDialog}
				onOpenChange={setShowHourTypesDialog}
				title="Seleccionar tipos de hora"
				description="Marca los tipos de hora que el usuario puede registrar"
				items={hourTypes}
				selectedIds={selectedHourTypeIDs}
				onSelectionChange={setSelectedHourTypeIDs}
			/>
		</>
	)
}
